<template>
  <section>
    <div class="w-full w-2/4 px-2 mb-2 md:mb-0 tw-col-span-1">
      <cl-form-group>
        <cl-form-label :label-for="t('licenses-purchased-legend')">
          {{ t("licenses-purchased-legend") }}
        </cl-form-label>
        <cl-form-input
          :id="'seats_purchased'"
          v-model:value="v$.licensesByGroup.seats_purchased.$model"
          :name="'seats_purchased'"
          :type="'number'"
          :placeholder="t('licenses-purchased-placeholder')"
          :data-test-id="`update-customer-licence-by-group`"
          :autofocus="isCurrentStep"
          :state="
            !v$.licensesByGroup.seats_purchased.$dirty
              ? undefined
              : !v$.licensesByGroup.seats_purchased.$error
          "
          :min="0"
          :max="100000"
          @on-keydown="guardNumberKeydown"
          @on-paste="guardNumberPaste"
        />
        <span
          v-if="v$.licensesByGroup.seats_purchased.$error"
          class="tw-text-system-error-500 tw-text-sm"
        >
          <span
            v-if="
              !v$.licensesByGroup.seats_purchased.maxValue.$response ||
              !v$.licensesByGroup.seats_purchased.minValue.$response ||
              !v$.licensesByGroup.seats_purchased.required.$response ||
              !v$.licensesByGroup.seats_purchased.integer.$response
            "
          >
            {{ t("licenses-purchased-validation") }}
          </span>
        </span>
      </cl-form-group>
    </div>
    <p class="tw-mb-4">{{ t("select-solutions") }}</p>
    <section
      :class="[
        isPhishServiceAvailable || hasServiceAvailableByMsp(PHISH_SERVICE)
          ? ''
          : 'tw-pointer-events-none tw-opacity-30',
      ]"
    >
      <div class="tw-flex tw-flex-row tw-justify-start">
        <cl-toggle
          id="update-emailSecurity-toggle"
          name="emailSecurity-switcher"
          class="tw-mr-3 tw-mb-2"
          :data-test-id="`edit-customer-email-security-toggle`"
          :value="v$.emailSecurity.$model.switcher"
          @update:value="
            (selected: boolean) => {
              form.emailSecurity.switcher = selected;

              if (isSEGCheckboxAvailable) {
                if (!selected) {
                  form.seg.switcher = false;
                }
              }
            }
          "
        >
          <span class="tw-ml-3 tw-text-base tw-font-normal">
            {{ t("email-security-solution") }}
          </span>
        </cl-toggle>
      </div>
      <div
        v-if="isConfirmationBannerVisible"
        class="tw-flex tw-gap-2 tw-bg-branding-blue-50 tw-border-2 tw-border-branding-blue-400 tw-p-2 tw-rounded-sm tw-mt-2 tw-mb-4"
      >
        <div class="tw-mt-1 tw-text-branding-blue-400">
          <cb-icon-information :width="18" />
        </div>
        <div>
          <div class="tw-mb-4">
            {{ t("disable-email-security-notice") }}
            <a
              :href="EXTERNAL_DOCS_LINKS.REMOVING_ES_SOLUTION"
              target="_blank"
              class="tw-inline-block tw-underline"
            >
              <span class="tw-flex tw-gap-1 tw-items-center">
                {{ t("learn-more") }}
                <cb-icon-external-link :width="16" />
              </span>
            </a>
          </div>
          <label class="tw-flex tw-items-center tw-gap-2 tw-text-sm">
            <cl-input-checkbox
              id="send_customer_statistics"
              data-test-id="edit-customer-disable-email-security-checkbox"
              :checked="isConfirmationChecked"
              :indeterminate="false"
              name="send_customer_statistics"
              @change="emit('toggleConfirm')"
            />
            {{ t("accept-disable-email-security") }}
          </label>
        </div>
      </div>
    </section>
    <!-- TODO: remove v-if="hasServiceAvailableByMsp(SEG_SERVICE)" once SEG is successfully tested by on prod BETA users -->
    <section
      v-if="hasServiceAvailableByMsp(SEG_SERVICE)"
      :class="[
        hasServiceAvailableByMsp(SEG_SERVICE)
          ? ''
          : 'tw-pointer-events-none tw-opacity-30',
      ]"
    >
      <div
        class="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-ml-[56px] tw-mb-2"
      >
        <cl-input-checkbox
          id="seg-switcher"
          data-test-id="edit-customer-seg-checkbox"
          :checked="form.seg.switcher"
          :indeterminate="false"
          name="seg-switcher"
          :disabled="
            !form.emailSecurity.switcher ||
            !hasServiceAvailableByMsp(SEG_SERVICE)
          "
          @change="form.seg.switcher = !form.seg.switcher"
        />
        <label for="seg-switcher" class="tw-italic">{{ t("seg-beta") }}</label>
      </div>
    </section>
    <section
      :class="[
        isPhishingSimulationServiceAvailable ||
        hasServiceAvailableByMsp(PHISHING_SIMULATION)
          ? ''
          : 'tw-pointer-events-none tw-opacity-30',
      ]"
      class="tw-mb-5 tw-mt-3"
    >
      <div class="tw-flex tw-flex-row tw-justify-start">
        <cl-toggle
          id="update-phishingSimulation-switcher"
          name="phishingSimulation-switcher"
          class="tw-mr-3"
          :data-test-id="`edit-customer-phishing-simulation-toggle`"
          :value="v$.phishingSimulation.$model.switcher"
          @update:value="
            (selected: boolean) => (form.phishingSimulation.switcher = selected)
          "
        >
          <span class="tw-ml-3 tw-text-base tw-font-normal">
            {{ t("phishing-simulation-label") }}
          </span>
        </cl-toggle>
        <span> </span>
      </div>
    </section>
    <!-- security awareness training -->
    <section
      :class="[
        isSatServiceAvailable || hasServiceAvailableByMsp(SAT_SERVICE)
          ? ''
          : 'tw-pointer-events-none tw-opacity-30',
      ]"
    >
      <div class="tw-flex tw-flex-row tw-justify-start">
        <cl-toggle
          id="update-sat-toggle"
          name="sat-switcher"
          class="tw-mr-3 tw-mb-2"
          :data-test-id="`edit-customer-sat-toggle`"
          :value="v$.sat.$model.switcher"
          @update:value="(selected: boolean) => (form.sat.switcher = selected)"
        >
          <span class="tw-ml-3 tw-text-base tw-font-normal">
            {{ t("sat-label") }}
          </span>
        </cl-toggle>
      </div>
    </section>
    <section
      :class="[
        isArcServiceAvailable || hasServiceAvailableByMsp(ARC_SERVICE)
          ? ''
          : 'tw-pointer-events-none tw-opacity-30',
      ]"
      class="tw-mt-3 tw-mb-8"
    >
      <div class="tw-mb-3 tw-flex tw-flex-row tw-justify-start">
        <cl-toggle
          id="emailArchiving-switcher"
          name="emailArchiving-switcher"
          class="tw-mr-3"
          :data-test-id="`edit-customer-email-archiving-toggle`"
          :value="form.emailArchiving.switcher"
          @update:value="
            (selected: boolean) => (form.emailArchiving.switcher = selected)
          "
        >
          <span class="tw-ml-3 tw-text-base tw-font-normal">
            {{ t("email-archiving-label") }}
          </span>
        </cl-toggle>
      </div>
    </section>
  </section>
</template>
<script lang="ts">
type Form = {
  licensesByGroup: {
    seats_purchased: number;
  };
  emailSecurity: {
    readonly seats_purchased: number;
    expires_at: string | undefined;
    switcher: boolean;
  };
  emailArchiving: {
    readonly seats_purchased: number;
    expires_at: string | undefined;
    switcher: boolean;
  };
  phishingSimulation: {
    readonly seats_purchased: number;
    expires_at: string | undefined;
    switcher: boolean;
  };
  sat: {
    readonly seats_purchased: number;
    expires_at: string | undefined;
    switcher: boolean;
  };
  seg: {
    expires_at: string | undefined;
    switcher: boolean;
  };
};

interface Props {
  form: Form;
  isCurrentStep: boolean;
  isConfirmationBannerVisible: boolean;
  listLicencesMsp: ResellerOrganisationResponse;
  isPhishServiceAvailable: boolean;
  isPhishingSimulationServiceAvailable: boolean;
  isSatServiceAvailable: boolean;
  isArcServiceAvailable: boolean;
  isConfirmationChecked: boolean;
}
</script>
<script setup lang="ts">
import { reactive, watch, watchEffect, computed } from "vue";
import { useVuelidate } from "@vuelidate/core";
import {
  integer,
  maxValue,
  minValue,
  numeric,
  required,
} from "@vuelidate/validators";
import { EXTERNAL_DOCS_LINKS } from "@/constants/externalDocsLinks.ts";
import { guardNumberKeydown, guardNumberPaste } from "@/utils/inputGuards";
import {
  ARC_SERVICE,
  PHISH_SERVICE,
  PHISHING_SIMULATION,
  SAT_SERVICE,
  SEG_SERVICE,
} from "@/utils/services";
import { ResellerOrganisationResponse } from "@/types/licenceSolutions.ts";
import { useI18n } from "vue-i18n";
import { useIsSEGCheckboxAvailable } from "@/composables/useFlags/useIsSEGCheckboxAvailable";

const { isSEGCheckboxAvailable } = useIsSEGCheckboxAvailable();

const props = defineProps<Props>();
const form = reactive<Form>({ ...props.form });
const { t } = useI18n();

const emit = defineEmits<{
  (e: "update:form", form: Form): void;
  (e: "formValidation", isValid: boolean): void;
  (e: "toggleConfirm"): void;
  (e: "isDisabled", value: boolean): void;
}>();

const rules = {
  licensesByGroup: {
    seats_purchased: {
      required,
      numeric,
      integer,
      minValue: minValue(1),
      maxValue: maxValue(100000),
    },
  },
  emailSecurity: {
    seats_purchased: { required, numeric, integer, maxValue: maxValue(100000) },
  },
  emailArchiving: {
    seats_purchased: { required, numeric, integer, maxValue: maxValue(100000) },
  },
  phishingSimulation: {
    seats_purchased: { required, numeric, integer, maxValue: maxValue(100000) },
  },
  sat: {
    seats_purchased: { required, numeric, integer, maxValue: maxValue(100000) },
  },
};

const v$ = useVuelidate(rules, form);

const hasServiceAvailableByMsp = (solution_uuid: string): boolean => {
  return props.listLicencesMsp.licences.some(
    (obj) => obj.solution_uuid === solution_uuid || false,
  );
};

const isSubmitDisabled = computed(() => {
  return v$.value.licensesByGroup.$invalid;
});

watch(form, (newValue) => {
  emit("update:form", newValue);
});

watch(
  () => props.isCurrentStep,
  (val) => {
    if (val) v$.value.$touch();
  },
  { immediate: true },
);

watchEffect(() => {
  emit("formValidation", v$.value.$invalid);
  emit("isDisabled", isSubmitDisabled.value);
});
</script>
